import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Card,
  InputNumber,
  Switch,
  message,
  Alert,
  Typography,
} from "antd";
import "./TriggerEditorV3.css";
import { referenceApi } from "../../services/api";
import { supabase } from "../../lib/supabase";
import {
  flattenConditionTree,
  readReference,
  writeReference,
} from "../../utils/raidAuthoring";

interface Props {
  entityCode: string;
  triggerId?: string;
  onSave: (trigger: any, conditionData: any, actions: any[]) => Promise<void>;
  onClose: () => void;
  initialData?: { trigger: any; conditionGroups: any; actions: any[] };
}
const newGroup = () => ({
  id: crypto.randomUUID(),
  type: "GROUP",
  group_name: "조건 그룹",
  logic_operator: "AND",
  children: [] as any[],
});
const newAction = () => ({
  id: crypto.randomUUID(),
  action_type: "CAST_SKILL",
  target_role: "LISTENER",
  action_params: {},
  delay_seconds: 0,
});

export const TriggerEditorV3: React.FC<Props> = ({
  entityCode,
  triggerId,
  onSave,
  onClose,
  initialData,
}) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [generatedCode] = useState(
    () => "TRG_" + crypto.randomUUID().replace(/-/g, ""),
  );
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [root, setRoot] = useState<any>(
    () => initialData?.conditionGroups || newGroup(),
  );
  const [actions, setActions] = useState<any[]>(
    () => initialData?.actions || [newAction()],
  );
  const [refs, setRefs] = useState<any>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const invalidJson = useRef(new Set<string>());
  const eventType = Form.useWatch("event_type", form);
  const listener = Form.useWatch("entity_code", form);
  const eventParams = Form.useWatch("event_params", form) || {};
  const participants = Form.useWatch("event_participants", form) || {};
  const ownerName =
    refs?.entities.find((e: any) => e.entity_code === listener)?.entity_name ||
    listener ||
    entityCode;
  const conditionCount = (group: any): number =>
    group.children.reduce(
      (sum: number, child: any) =>
        sum + (child.type === "GROUP" ? conditionCount(child) : 1),
      0,
    );
  const roleName = (code: string) =>
    ({
      ANY: "제한 없음",
      PLAYER: "플레이어",
      GROUND: "바닥",
      NONE: "없음",
      LISTENER: "이 몬스터",
    })[code] ||
    refs?.entities.find((e: any) => e.entity_code === code)?.entity_name ||
    refs?.roles.find((r: any) => r.role_code === code)?.role_name ||
    code;
  useEffect(() => {
    let active = true;
    Promise.all([
      referenceApi.fetchEventTypes(),
      referenceApi.fetchParticipantTypes(),
      supabase
        .from("inf_condition_value_type_renewed")
        .select("*")
        .then((r) => {
          if (r.error) throw r.error;
          return r.data || [];
        }),
      referenceApi.fetchConditionOperators(),
      referenceApi.fetchActionTypes(),
      referenceApi.fetchSkills(),
      referenceApi.fetchEffects(),
      referenceApi.fetchEntities(),
    ])
      .then(
        ([
          events,
          roles,
          values,
          operators,
          actionTypes,
          skills,
          effects,
          entities,
        ]) => {
          if (active)
            setRefs({
              events,
              roles,
              values,
              operators,
              actionTypes,
              skills,
              effects,
              entities,
            });
        },
      )
      .catch((e) => {
        if (active) setError(e.message || "참조 데이터를 불러오지 못했습니다.");
      });
    return () => {
      active = false;
    };
  }, []);
  const choices = (rows: any[], code: string, name: string) =>
    rows.map((r) => ({
      value: r[code],
      label: r[code] === "LISTENER" ? "이 몬스터" : r[name] || r[code],
      searchText: `${r[name] || ""} ${r[code]}`,
      title: r[code],
    }));
  const picker = (
    value: any,
    onChange: (v: any) => void,
    options: any[],
    label: string,
    clear = false,
  ) => (
    <Select
      aria-label={label}
      placeholder={label}
      value={value}
      onChange={onChange}
      options={options}
      showSearch
      optionFilterProp="searchText"
      filterOption={(input, option) =>
        String(option?.searchText || option?.label || "")
          .toLowerCase()
          .includes(input.toLowerCase())
      }
      allowClear={clear}
      style={{ width: "100%" }}
    />
  );
  const resources = (kind: "skill" | "effect") =>
    choices(
      refs?.[kind === "skill" ? "skills" : "effects"] || [],
      "key",
      "name",
    );
  const updateAction = (index: number, fields: any) =>
    setActions((prev) =>
      prev.map((a, i) => (i === index ? { ...a, ...fields } : a)),
    );
  const actionReference = (a: any, i: number, kind: "skill" | "effect") =>
    picker(
      readReference(a.action_params, kind),
      (value) =>
        updateAction(i, {
          action_params: writeReference(a.action_params, kind, value),
        }),
      resources(kind),
      kind === "skill" ? "연결 스킬" : "연결 효과",
    );
  const parameter = (a: any, i: number, key: string, value: any) =>
    updateAction(i, { action_params: { ...a.action_params, [key]: value } });
  const groupEditor = (
    group: any,
    change: (g: any) => void,
    depth = 0,
  ): React.ReactNode => (
    <Card size="small" style={{ marginTop: 8 }}>
      <div className="trigger-condition-toolbar">
        <details>
          <summary>그룹 이름</summary>
          <Input
            aria-label="조건 그룹 이름"
            value={group.group_name}
            onChange={(e) => change({ ...group, group_name: e.target.value })}
          />
        </details>
        <Select
          aria-label="조건 결합"
          value={group.logic_operator}
          options={[
            { value: "AND", label: "모두 충족 (AND)" },
            { value: "OR", label: "하나 충족 (OR)" },
          ]}
          onChange={(logic_operator) => change({ ...group, logic_operator })}
        />
        <Button
          onClick={() =>
            change({
              ...group,
              children: [
                ...group.children,
                {
                  id: crypto.randomUUID(),
                  type: "CONDITION",
                  participant_role_code: "LISTENER",
                  value_type_code: "HP_PERCENT",
                  operator_code: "LESS_THAN_OR_EQUAL",
                  compare_value: "50",
                },
              ],
            })
          }
        >
          조건 추가
        </Button>
        <Button
          disabled={depth >= 8}
          onClick={() =>
            change({ ...group, children: [...group.children, newGroup()] })
          }
        >
          그룹 추가
        </Button>
      </div>
      {group.children.map((child: any, index: number) => {
        const update = (next: any) =>
          change({
            ...group,
            children: group.children.map((c: any, i: number) =>
              i === index ? next : c,
            ),
          });
        return (
          <div key={child.id} style={{ marginTop: 8 }}>
            {child.type === "GROUP" ? (
              groupEditor(child, update, depth + 1)
            ) : (
              <div className="trigger-condition-fields">
                {picker(
                  child.participant_role_code,
                  (v) => update({ ...child, participant_role_code: v }),
                  choices(refs?.roles || [], "role_code", "role_name"),
                  "조건 대상",
                )}
                {picker(
                  child.value_type_code,
                  (v) =>
                    update({
                      ...child,
                      value_type_code: v,
                      custom_property_name: undefined,
                      effect_code: undefined,
                    }),
                  choices(
                    refs?.values || [],
                    "value_type_code",
                    "value_type_name",
                  ),
                  "검사 값",
                )}
                {child.value_type_code === "CUSTOM_PROPERTY" && (
                  <Input
                    aria-label="속성 이름"
                    placeholder="속성 이름"
                    value={child.custom_property_name}
                    onChange={(e) =>
                      update({ ...child, custom_property_name: e.target.value })
                    }
                  />
                )}
                {child.value_type_code === "EFFECT_STACK" &&
                  picker(
                    child.effect_code,
                    (v) => update({ ...child, effect_code: v }),
                    resources("effect"),
                    "중첩 효과",
                  )}
                {picker(
                  child.operator_code,
                  (v) => update({ ...child, operator_code: v }),
                  choices(
                    refs?.operators || [],
                    "operator_code",
                    "operator_name",
                  ),
                  "비교 연산자",
                )}
                <Input
                  aria-label="비교값"
                  placeholder="비교값"
                  value={child.compare_value}
                  onChange={(e) =>
                    update({ ...child, compare_value: e.target.value })
                  }
                />
              </div>
            )}
            <Button
              danger
              size="small"
              onClick={() =>
                change({
                  ...group,
                  children: group.children.filter(
                    (_: any, i: number) => i !== index,
                  ),
                })
              }
            >
              삭제
            </Button>
          </div>
        );
      })}
    </Card>
  );
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const values = await form.validateFields();
      if (actions.some((a, i) => invalidJson.current.has(String(a.id || i))))
        throw Error("잘못된 액션 JSON을 수정한 후 저장해주세요.");
      if (!actions.length) throw new Error("액션을 한 개 이상 추가해주세요.");
      const conditions = flattenConditionTree(root);
      for (const c of conditions.conditions) {
        if (c.nested_group_id) continue;
        if (
          !c.participant_role_code ||
          !c.value_type_code ||
          !c.operator_code ||
          c.compare_value === ""
        )
          throw new Error("조건의 대상·검사 값·연산자·비교값을 확인해주세요.");
        if (
          c.value_type_code === "CUSTOM_PROPERTY" &&
          !c.custom_property_name?.trim()
        )
          throw new Error("커스텀 속성 이름이 필요합니다.");
        if (
          c.value_type_code === "EFFECT_STACK" &&
          !refs.effects.some((e: any) => e.key === c.effect_code)
        )
          throw new Error("중첩을 검사할 효과를 선택해주세요.");
      }
      for (const a of actions) {
        if (!a.action_type || !a.target_role)
          throw new Error("모든 액션의 종류와 대상을 선택해주세요.");
        const keys = ["CAST_SKILL", "ADD_SKILL", "REMOVE_SKILL"].includes(
          a.action_type,
        )
          ? [readReference(a.action_params, "skill")]
          : a.action_type === "REPLACE_SKILL"
            ? [a.action_params.from_skill, a.action_params.to_skill]
            : [];
        if (keys.some((k) => !refs.skills.some((s: any) => s.key === k)))
          throw new Error("액션에서 실제 스킬을 선택해주세요.");
        if (
          ["APPLY_EFFECT", "REMOVE_EFFECT"].includes(a.action_type) &&
          !refs.effects.some(
            (e: any) => e.key === readReference(a.action_params, "effect"),
          )
        )
          throw new Error("액션에서 실제 효과를 선택해주세요.");
      }
      await onSave(
        {
          ...values,
          id: triggerId,
          event_params: values.event_params || {},
          event_participants: {
            ...values.event_participants,
            listener: values.entity_code,
          },
          priority: values.priority ?? 100,
          max_trigger_count: values.max_trigger_count ?? 1,
        },
        conditions,
        actions.map((a, i) => ({
          action_order: i + 1,
          action_type: a.action_type,
          target_role: a.target_role,
          action_params: a.action_params || {},
          delay_seconds: a.delay_seconds ?? 0,
        })),
      );
    } catch (e: any) {
      const text = e.message || "필수 입력값을 확인해주세요.";
      if (
        e.errorFields?.some((field: any) =>
          ["entity_code", "trigger_code", "priority"].includes(field.name[0]),
        )
      )
        setAdvancedOpen(true);
      setError(text);
      messageApi.error(text);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      title={triggerId ? "트리거 수정" : "트리거 추가"}
      open
      width={820}
      style={{ top: 24 }}
      className="trigger-editor"
      styles={{
        body: {
          maxHeight: "calc(100vh - 160px)",
          overflowY: "auto",
          paddingRight: 8,
        },
      }}
      onCancel={onClose}
      onOk={save}
      confirmLoading={saving}
      okButtonProps={{ disabled: !refs }}
      okText="전체 저장"
      cancelText="취소"
    >
      {contextHolder}
      {error && <Alert type="error" showIcon message={error} />}
      <Typography.Paragraph type="secondary">
        {ownerName}의 행동 규칙 · 언제 실행하고 어떤 행동을 할지 정하세요.
      </Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          entity_code: entityCode,
          is_enabled: true,
          is_repeatable: false,
          max_trigger_count: 1,
          priority: 100,
          trigger_code: generatedCode,
          event_params: {},
          event_participants: {
            listener: entityCode,
            event_source: "ANY",
            event_target: "ANY",
          },
          ...initialData?.trigger,
        }}
      >
        <div className="trigger-name-row">
          <Form.Item
            name="trigger_name"
            label="규칙 이름"
            rules={[
              { required: true, message: "규칙 이름을 입력해주세요." },
              { max: 100 },
            ]}
          >
            <Input placeholder="예: 내려찍기 후 얼음 가시 사용" />
          </Form.Item>
          <Form.Item name="is_enabled" label="사용" valuePropName="checked">
            <Switch checkedChildren="켬" unCheckedChildren="끔" />
          </Form.Item>
        </div>
        <div className="trigger-rule-grid">
          <div>
            <section className="trigger-section" aria-label="실행 시점">
              <Typography.Title level={5}>1. 언제 실행하나요?</Typography.Title>
              <Form.Item
                name="event_type"
                label="실행 시점"
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  optionFilterProp="searchText"
                  options={choices(
                    refs?.events || [],
                    "event_code",
                    "event_name",
                  )}
                  onChange={() => form.setFieldValue("event_params", {})}
                />
              </Form.Item>
              <Form.Item name="event_params" hidden>
                <Input />
              </Form.Item>
              <Form.Item name="event_participants" hidden>
                <Input />
              </Form.Item>
              {eventType?.startsWith("ON_SKILL_") && (
                <Form.Item label="감지할 스킬 (비우면 모든 스킬)">
                  {picker(
                    readReference(eventParams, "skill"),
                    (v) =>
                      form.setFieldValue(
                        "event_params",
                        writeReference(eventParams, "skill", v),
                      ),
                    resources("skill"),
                    "감지할 스킬",
                    true,
                  )}
                </Form.Item>
              )}
              {eventType?.startsWith("ON_EFFECT_") && (
                <Form.Item label="감지할 효과 (비우면 모든 효과)">
                  {picker(
                    readReference(eventParams, "effect"),
                    (v) =>
                      form.setFieldValue(
                        "event_params",
                        writeReference(eventParams, "effect", v),
                      ),
                    resources("effect"),
                    "감지할 효과",
                    true,
                  )}
                </Form.Item>
              )}
              {eventType === "ON_PERIODIC_TIMER" && (
                <Form.Item label="실행 간격 (초)">
                  <InputNumber
                    min={0.1}
                    value={eventParams.interval}
                    onChange={(v) =>
                      form.setFieldValue("event_params", {
                        ...eventParams,
                        interval: v,
                      })
                    }
                  />
                </Form.Item>
              )}
              {eventType === "ON_PROPERTY_CHANGE" && (
                <Form.Item label="감지할 속성">
                  <Input
                    value={readReference(eventParams, "property")}
                    onChange={(e) =>
                      form.setFieldValue(
                        "event_params",
                        writeReference(eventParams, "property", e.target.value),
                      )
                    }
                  />
                </Form.Item>
              )}
              <details className="trigger-disclosure">
                <summary>
                  감지 대상 좁히기{" "}
                  <Typography.Text type="secondary">
                    · 유발자: {roleName(participants.event_source || "ANY")} /
                    대상: {roleName(participants.event_target || "ANY")}
                  </Typography.Text>
                </summary>
                <div className="trigger-field-grid">
                  {["event_source", "event_target"].map((key) => (
                    <Form.Item
                      key={key}
                      label={
                        key === "event_source" ? "이벤트 유발자" : "이벤트 대상"
                      }
                    >
                      {picker(
                        participants[key],
                        (v) =>
                          form.setFieldValue("event_participants", {
                            ...participants,
                            [key]: v,
                          }),
                        Array.from(
                          new Map(
                            [
                              ...choices(
                                refs?.entities || [],
                                "entity_code",
                                "entity_name",
                              ),
                              ...choices(
                                refs?.roles || [],
                                "role_code",
                                "role_name",
                              ),
                              ...[
                                "ANY",
                                "PLAYER",
                                "GROUND",
                                "NONE",
                                participants[key],
                              ]
                                .filter(Boolean)
                                .map((v) => ({ value: v, label: roleName(v) })),
                            ].map((o) => [o.value, o]),
                          ).values(),
                        ),
                        key,
                      )}
                    </Form.Item>
                  ))}
                </div>
              </details>
            </section>
            <details className="trigger-section trigger-disclosure">
              <summary>
                <strong>2. 추가 조건</strong>{" "}
                <Typography.Text type="secondary">
                  ·{" "}
                  {conditionCount(root)
                    ? conditionCount(root) + "개 조건 설정됨"
                    : "없음 — 실행 시점만 맞으면 실행"}
                </Typography.Text>
              </summary>
              <Typography.Paragraph type="secondary">
                HP나 효과 중첩처럼 실행을 제한할 조건이 있을 때 추가하세요.
              </Typography.Paragraph>
              {groupEditor(root, setRoot)}
            </details>
          </div>
          <section className="trigger-section" aria-label="실행할 행동">
            <Typography.Title level={5}>
              3. 어떤 행동을 하나요?
            </Typography.Title>
            {actions.map((a, i) => (
              <Card
                key={a.id || i}
                size="small"
                title={
                  i +
                  1 +
                  ". " +
                  (refs?.actionTypes.find(
                    (t: any) => t.action_code === a.action_type,
                  )?.action_name || "행동 선택")
                }
                style={{ marginBottom: 12 }}
                extra={
                  <Button
                    danger
                    onClick={() =>
                      setActions(actions.filter((_, index) => index !== i))
                    }
                  >
                    삭제
                  </Button>
                }
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div className="trigger-field-grid">
                    <Form.Item label="행동">
                      {picker(
                        a.action_type,
                        (v) =>
                          updateAction(i, {
                            action_type: v,
                            action_params: {},
                          }),
                        choices(
                          refs?.actionTypes || [],
                          "action_code",
                          "action_name",
                        ),
                        "액션 종류",
                      )}
                    </Form.Item>
                    <Form.Item label="대상">
                      {picker(
                        a.target_role,
                        (v) => updateAction(i, { target_role: v }),
                        choices(refs?.roles || [], "role_code", "role_name"),
                        "액션 대상",
                      )}
                    </Form.Item>
                  </div>
                  {["CAST_SKILL", "ADD_SKILL", "REMOVE_SKILL"].includes(
                    a.action_type,
                  ) && (
                    <Form.Item label="사용할 스킬">
                      {actionReference(a, i, "skill")}
                    </Form.Item>
                  )}
                  {a.action_type === "REPLACE_SKILL" && (
                    <>
                      {picker(
                        a.action_params.from_skill,
                        (v) => parameter(a, i, "from_skill", v),
                        resources("skill"),
                        "교체 전 스킬",
                      )}
                      {picker(
                        a.action_params.to_skill,
                        (v) => parameter(a, i, "to_skill", v),
                        resources("skill"),
                        "교체 후 스킬",
                      )}
                    </>
                  )}
                  {["APPLY_EFFECT", "REMOVE_EFFECT"].includes(
                    a.action_type,
                  ) && (
                    <Form.Item label="효과">
                      {actionReference(a, i, "effect")}
                    </Form.Item>
                  )}
                  {a.action_type === "APPLY_EFFECT" && (
                    <InputNumber
                      placeholder="지속시간 (초, -1: 영구)"
                      min={-1}
                      value={a.action_params.duration}
                      onChange={(v) => parameter(a, i, "duration", v)}
                    />
                  )}
                  {a.action_type === "SPAWN_ENTITY" && (
                    <>
                      {picker(
                        a.action_params.entity_code,
                        (v) => parameter(a, i, "entity_code", v),
                        choices(
                          refs?.entities || [],
                          "entity_code",
                          "entity_name",
                        ),
                        "소환 몬스터·오브젝트",
                      )}
                      <InputNumber
                        placeholder="소환 수"
                        min={1}
                        value={a.action_params.count}
                        onChange={(v) => parameter(a, i, "count", v)}
                      />
                    </>
                  )}
                  {a.action_type === "MANIPULATE_PROPERTY" && (
                    <>
                      <Input
                        placeholder="속성 이름"
                        value={readReference(a.action_params, "property")}
                        onChange={(e) =>
                          updateAction(i, {
                            action_params: writeReference(
                              a.action_params,
                              "property",
                              e.target.value,
                            ),
                          })
                        }
                      />
                      {picker(
                        a.action_params.operation,
                        (v) => parameter(a, i, "operation", v),
                        [
                          "SET",
                          "INCREMENT",
                          "DECREMENT",
                          "MULTIPLY",
                          "DIVIDE",
                          "RESET",
                        ].map((v) => ({ value: v, label: v })),
                        "속성 연산",
                      )}
                      <Input
                        placeholder="값 또는 기존 계산식"
                        value={a.action_params.value}
                        onChange={(e) => {
                          const v = e.target.value;
                          parameter(
                            a,
                            i,
                            "value",
                            v.trim() !== "" && Number.isFinite(Number(v))
                              ? Number(v)
                              : v,
                          );
                        }}
                      />
                    </>
                  )}
                  {a.action_type === "SHOW_MESSAGE" && (
                    <Input.TextArea
                      placeholder="표시할 메시지"
                      value={a.action_params.message}
                      onChange={(e) =>
                        parameter(a, i, "message", e.target.value)
                      }
                    />
                  )}
                  <details className="trigger-disclosure">
                    <summary>
                      행동 세부 설정{" "}
                      <Typography.Text type="secondary">
                        ·{" "}
                        {a.delay_seconds
                          ? a.delay_seconds + "초 후 실행"
                          : "즉시 실행"}
                      </Typography.Text>
                    </summary>
                    <Form.Item label="지연 시간 (초)">
                      {" "}
                      <InputNumber
                        aria-label="지연 시간 (초)"
                        placeholder="지연 시간 (초)"
                        min={0}
                        value={a.delay_seconds}
                        onChange={(v) => updateAction(i, { delay_seconds: v })}
                      />
                    </Form.Item>
                    <details className="trigger-disclosure">
                      <summary>전체 파라미터 (고급)</summary>
                      <Input.TextArea
                        key={JSON.stringify(a.action_params)}
                        defaultValue={JSON.stringify(a.action_params, null, 2)}
                        rows={5}
                        onChange={(e) => {
                          try {
                            const value = JSON.parse(e.target.value);
                            if (
                              !value ||
                              Array.isArray(value) ||
                              typeof value !== "object"
                            )
                              throw Error();
                            invalidJson.current.delete(String(a.id || i));
                          } catch {
                            invalidJson.current.add(String(a.id || i));
                          }
                        }}
                        onBlur={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            if (
                              !parsed ||
                              Array.isArray(parsed) ||
                              typeof parsed !== "object"
                            )
                              throw Error();
                            updateAction(i, { action_params: parsed });
                          } catch {
                            messageApi.error(
                              "파라미터는 JSON 객체여야 합니다. 변경값을 적용하지 않았습니다.",
                            );
                          }
                        }}
                      />
                    </details>
                  </details>
                </Space>
              </Card>
            ))}
            <Button
              type="dashed"
              block
              onClick={() => setActions((prev) => [...prev, newAction()])}
            >
              행동 추가
            </Button>
          </section>
        </div>
        <div className="trigger-repeat-row">
          <Form.Item
            name="is_repeatable"
            label="조건이 맞을 때 반복 실행"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="max_trigger_count"
            label="최대 실행 횟수 (0: 제한 없음)"
          >
            <InputNumber min={0} precision={0} />
          </Form.Item>
        </div>
        <details
          className="trigger-section trigger-disclosure"
          open={advancedOpen}
          onToggle={(e) => setAdvancedOpen(e.currentTarget.open)}
        >
          <summary>
            <strong>고급 설정</strong>{" "}
            <Typography.Text type="secondary">
              · 소유자, 코드, 우선순위, 메모
            </Typography.Text>
          </summary>
          <Form.Item
            name="entity_code"
            label="규칙을 소유한 몬스터·오브젝트"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              optionFilterProp="searchText"
              options={choices(
                refs?.entities || [],
                "entity_code",
                "entity_name",
              )}
              onChange={(v) =>
                form.setFieldValue("event_participants", {
                  ...participants,
                  listener: v,
                })
              }
            />
          </Form.Item>

          <div className="trigger-field-grid">
            <Form.Item
              name="trigger_code"
              label="트리거 코드"
              extra="새 규칙은 코드가 자동으로 생성됩니다."
              rules={[{ required: true }, { max: 50 }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="priority" label="우선순위">
              <InputNumber min={0} precision={0} />
            </Form.Item>
          </div>
          <Form.Item name="description" label="설명">
            <Input.TextArea />
          </Form.Item>
        </details>
      </Form>
    </Modal>
  );
};
